"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.probes = exports.PROBE_NAMES = exports.syntheticRoundtripDiff = void 0;
const common_1 = require("@finley-technologies/common");
const bbplGraph_1 = require("../bbplGraph");
const xlsx_1 = require("../bbplGraph/xlsx");
const convertor_1 = require("../convertor");
const dbBasicGraphUtils_1 = require("../graphUtils/dbBasicGraphUtils");
const dbGraphUtils_1 = require("../graphUtils/dbGraphUtils");
const templateClone_1 = require("../graphUtils/templateClone");
const synthetic_1 = require("../synthetic");
const tapeBuilder_1 = require("../tapeBuilder");
const canonicalize_1 = require("./canonicalize");
const graphDiff_1 = require("./graphDiff");
const RAW_DATA_DICTIONARY_TYPES = [
    'rawLoanTape',
    'rawSupplementalTape',
    'rawRepaymentTape',
    'rawCashBalanceFeed',
];
const CLONE_SUFFIX = 'Fp';
/**
 * Deterministic diff between a stored graph and its detach → attach roundtrip
 * (configuration keys sorted, slugs NOT normalized). Throws if either
 * conversion throws.
 */
const syntheticRoundtripDiff = (graph) => {
    const uiGraph = (0, synthetic_1.detachSyntheticRecords)({
        dataTapes: graph.artifacts,
        dataTapeDependencies: graph.dependencies,
    });
    const reattached = (0, synthetic_1.attachSyntheticRecords)(uiGraph);
    // Only object-key ordering is normalized — slugs are left as-is so the diff
    // faithfully records what the roundtrip changed.
    return (0, graphDiff_1.diffGraphs)({
        graph1: (0, graphDiff_1.normalizeGraph)(graph),
        graph2: (0, graphDiff_1.normalizeGraph)({
            artifacts: reattached.dataTapes,
            dependencies: reattached.dataTapeDependencies,
        }),
    });
};
exports.syntheticRoundtripDiff = syntheticRoundtripDiff;
// Mirrors the production load path (finleycore `artifactsDocumentString`
// resolver → dashboard `artifactsDocumentToTapeBuilderGraph`).
const loadUiGraph = (document) => {
    const dbGraph = (0, synthetic_1.detachSyntheticRecords)({
        dataTapes: document.artifacts,
        dataTapeDependencies: document.dependencies,
    });
    const artifactsDocument = convertor_1.convertor.artifactsDocumentFromDbRecords({
        dbGraph,
        dbView: false,
        artifactDocumentId: document.id,
    });
    return convertor_1.convertor.artifactsDocumentToTapeBuilder({ artifactsDocument }).graph;
};
const idAndSlugs = (idToSlug) => [...idToSlug.entries()].map(([id, slug]) => ({ id, slug }));
// Load → save → server attach, i.e. what an unedited "save" in the tape
// builder would write. Save runs every UI validation, so an invalid result
// is reported with its sorted errors.
const uiRoundtripDiff = (document) => {
    const result = convertor_1.convertor.tapeBuilderToArtifactsDocument({
        graph: loadUiGraph(document),
        creditFacilities: idAndSlugs(document.cfIdToSlugMap),
        loanProducts: idAndSlugs(document.lpIdToSlugMap),
        artifactDocumentId: document.id,
        createTapeBuilder: tapeBuilder_1.createTapeBuilder,
    });
    if (!result.isValid) {
        return `INVALID:\n${(0, canonicalize_1.sortedStrings)(result.errors).join('\n')}`;
    }
    const records = convertor_1.convertor.dbRecordsFromArtifactsDocument({
        artifactsDocument: result.artifactsDocument,
    });
    (0, common_1.finleyAssert)(!records.dbView, 'UI save must produce a non-dbView document');
    const saved = (0, synthetic_1.attachSyntheticRecords)(records);
    return (0, graphDiff_1.diffGraphs)({
        graph1: (0, graphDiff_1.normalizeGraph)(document),
        graph2: (0, graphDiff_1.normalizeGraph)({
            artifacts: saved.dataTapes,
            dependencies: saved.dataTapeDependencies,
        }),
    });
};
// Clones the way `clone` does, with every cf/lp slug and id mapped to a
// suffixed value, so a changed remap shows up as an unsuffixed leftover.
const templateCloneView = (document) => {
    const cloneMap = new Map();
    [document.cfIdToSlugMap, document.lpIdToSlugMap].forEach((idToSlug) => idToSlug.forEach((slug, id) => {
        cloneMap.set(slug, `${slug}${CLONE_SUFFIX}`);
        cloneMap.set(id, `${id}${CLONE_SUFFIX}`);
    }));
    const clone = (graph) => {
        try {
            return JSON.parse((0, canonicalize_1.canonicalizeDbGraph)((0, templateClone_1.createTemplateUtils)({
                artifactDocumentId: document.id,
                ...graph,
            }).cloneTemplateGraph({
                newArtifactDocumentId: `${document.id}${CLONE_SUFFIX}`,
                cloneMap,
            })));
        }
        catch (error) {
            return `ERROR: ${error instanceof Error ? error.message : String(error)}`;
        }
    };
    // One unsupported node type fails the whole clone, so each artifact is also
    // cloned on its own to keep every node type's clone logic covered.
    const perArtifact = [...document.artifacts]
        .sort((left, right) => (left.artifactSlug < right.artifactSlug ? -1 : 1))
        .map((artifact) => ({
        artifactSlug: artifact.artifactSlug,
        clone: clone({ artifacts: [artifact], dependencies: [] }),
    }));
    return (0, canonicalize_1.canonicalJson)({
        graph: clone({
            artifacts: document.artifacts,
            dependencies: document.dependencies,
        }),
        perArtifact,
    }, new Map());
};
const FUNDING_VERIFICATION_ACCESSORS = [
    'servicerReportArtifact',
    'waiverTapeArtifact',
    'receivableResponseTapeArtifact',
    'borrowingBaseReportArtifact',
    'verificationReportArtifact',
    'approvalReportArtifact',
    'fundingRequestArtifact',
];
const accessorSlug = (accessor) => {
    try {
        return accessor().artifactSlug;
    }
    catch (error) {
        return `ERROR: ${error instanceof Error ? error.message : String(error)}`;
    }
};
const classifiedGraphView = (graph, idLabels) => ({
    type: graph.type,
    artifactSlugs: (0, canonicalize_1.sortedStrings)(graph.artifacts.map((a) => a.artifactSlug)),
    dependencies: (0, canonicalize_1.sortedStrings)(graph.dependencies.map(({ inputArtifactId, outputArtifactId }) => `${idLabels.get(inputArtifactId)} → ${idLabels.get(outputArtifactId)}`)),
    creditFacilitySlugs: (0, canonicalize_1.sortedStrings)(graph.creditFacilitySlugs),
    loanProductSlugs: (0, canonicalize_1.sortedStrings)(graph.loanProductSlugs),
    disjointGraphCount: graph.disjointGraphs.length,
    ...(graph.type === 'fundingVerification'
        ? {
            creditFacilitySlug: graph.creditFacilitySlug,
            accessors: Object.fromEntries(FUNDING_VERIFICATION_ACCESSORS.map((name) => [
                name,
                accessorSlug(graph[name]),
            ])),
        }
        : {}),
});
const classificationView = (document) => {
    const idLabels = (0, canonicalize_1.artifactIdLabels)(document.artifacts);
    const graphs = bbplGraph_1.BbplGraph.PrivateForArtifactModule.getGraphs({
        ...document,
        pipelineDefinitionId: 'verification',
        isWip: null,
        lastMeshChangeArtifactDocumentId: document.id,
    })
        .map((graph) => (0, canonicalize_1.canonicalJson)(classifiedGraphView(graph, idLabels), idLabels))
        .sort();
    return `[${graphs.join(',')}]`;
};
// Per artifact: every recursive input/output as computed by each traversal
// implementation. The bbplGraph and graphUtils traversals should agree; the
// probe records both so porting one onto the other is checked.
const traversalView = (document) => {
    const graph = {
        artifacts: document.artifacts,
        dependencies: document.dependencies,
    };
    const basic = (0, dbBasicGraphUtils_1.createBasicGraphUtils)({
        artifactDocumentId: document.id,
        version: document.version,
        artifacts: document.artifacts,
        dependencies: document.dependencies,
        cfIdToSlugMap: document.cfIdToSlugMap,
        lpIdToSlugMap: document.lpIdToSlugMap,
    });
    const slugById = new Map(document.artifacts.map((a) => [a.id, a.artifactSlug]));
    const slugs = (ids) => (0, canonicalize_1.sortedStrings)(ids.map((id) => slugById.get(id) ?? `missing:${id}`));
    const view = [...document.artifacts]
        .sort((left, right) => (left.artifactSlug < right.artifactSlug ? -1 : 1))
        .map(({ id, artifactSlug }) => ({
        artifactSlug,
        bbplDependencies: slugs(bbplGraph_1.BbplGraph.findDependenciesRecursively(graph, [id]).map((a) => a.id)),
        recursiveInputs: slugs(basic.getRecursiveInputs(id)),
        recursiveOutputs: slugs(basic.getRecursiveOutputs(id)),
    }));
    return (0, canonicalize_1.canonicalJson)(view, new Map());
};
const meshGroupingView = (document) => {
    const graphUtils = (0, dbGraphUtils_1.createGraphUtils)({
        ...document,
        borrowerOrganizationId: 'verificationBorrowerOrganization',
        implementationId: 'verificationImplementation',
        borrowerSlug: 'verificationBorrower',
    });
    // Superuser view: every facility in the graph is in scope.
    graphUtils.limitScopeToCFSlugs([...document.cfIdToSlugMap.values()]);
    const idLabels = (0, canonicalize_1.artifactIdLabels)(document.artifacts);
    idLabels.set(document.id, 'artifactDocument');
    const meshDocuments = graphUtils
        .getMeshDocuments()
        .map((meshDocument) => (0, canonicalize_1.canonicalJson)(meshDocument, idLabels))
        .sort();
    return (0, canonicalize_1.canonicalJson)({
        meshDocuments,
        cfLpRelationships: graphUtils.getCfLpRelationships(),
        lpCfRelationships: graphUtils.getLpCfRelationships(),
        lpCfGroupings: graphUtils.getLpCfGroupings(),
    }, idLabels);
};
// The data dictionary shown next to each raw upload in the data-pipeline UI.
const dataDictionaryView = (document) => {
    const graph = {
        artifacts: document.artifacts,
        dependencies: document.dependencies,
    };
    const view = document.artifacts
        .filter(({ type }) => RAW_DATA_DICTIONARY_TYPES.includes(type))
        .sort((left, right) => (left.artifactSlug < right.artifactSlug ? -1 : 1))
        .map(({ id, artifactSlug }) => ({
        artifactSlug,
        xlsx: xlsx_1.XlsxExtraction.getXlsxFieldGroups(graph, id),
        csv: xlsx_1.XlsxExtraction.getCsvFieldGroups(graph, id),
    }));
    return (0, canonicalize_1.canonicalJson)(view, (0, canonicalize_1.artifactIdLabels)(document.artifacts));
};
exports.PROBE_NAMES = [
    'syntheticRoundtrip',
    'uiGraph',
    'uiRoundtrip',
    'templateClone',
    'classification',
    'traversal',
    'meshGrouping',
    'dataDictionary',
];
exports.probes = {
    syntheticRoundtrip: {
        kind: 'diff',
        description: 'stored graph vs detach → attach (same output as validateRoundtrip)',
        run: exports.syntheticRoundtripDiff,
    },
    uiGraph: {
        kind: 'canonical',
        description: 'UI operation graph produced by the production load path',
        run: (document) => (0, canonicalize_1.canonicalizeUiGraph)({
            graph: loadUiGraph(document),
            artifacts: document.artifacts,
        }),
    },
    uiRoundtrip: {
        kind: 'diff',
        description: 'stored graph vs load → save (all UI validations) → server attach',
        run: uiRoundtripDiff,
    },
    templateClone: {
        kind: 'canonical',
        description: 'cloneTemplateGraph with every cf/lp slug and id suffixed',
        run: templateCloneView,
    },
    classification: {
        kind: 'canonical',
        description: 'bbplGraph getGraphs classification and typed accessors',
        run: classificationView,
    },
    traversal: {
        kind: 'canonical',
        description: 'recursive inputs/outputs per artifact (bbplGraph + graphUtils)',
        run: traversalView,
    },
    meshGrouping: {
        kind: 'canonical',
        description: 'graphUtils mesh documents and cf/lp groupings',
        run: meshGroupingView,
    },
    dataDictionary: {
        kind: 'canonical',
        description: 'xlsx/csv data-dictionary field groups per raw upload',
        run: dataDictionaryView,
    },
};
//# sourceMappingURL=probes.js.map